import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';

import {
  ConvertableToDateTime,
  DateTime,
  OptionalTimeRange,
  TimeUnit,
} from '@cast/shared-types';
import { timeRange } from '@cast/utils';

import { Timestamp, TimeSeriesEntry } from 'types/metrics';
import { Ranked } from 'types/top-items';

type FillerFn<T> = (timestamp: DateTime) => T & { timestamp: Timestamp };
type Filler<T> = Omit<T, 'timestamp'> | FillerFn<T>;

type FilledValue<
  VALUE extends TimeSeriesEntry<object>,
  FILLER = Filler<Partial<VALUE>>
> = FILLER extends (...args: any) => any
  ? ReturnType<FILLER>
  : FILLER & { timestamp: VALUE['timestamp'] };

export type WithFilledGaps<
  VALUE extends TimeSeriesEntry<object>,
  FILLER = Filler<Partial<VALUE>>
> = VALUE | FilledValue<VALUE, FILLER>;

type FillGapsArgs<
  VALUE extends TimeSeriesEntry<object>,
  FILLER = Filler<Partial<VALUE>>
> = {
  timeSeries: VALUE[];
  dateRange: OptionalTimeRange<ConvertableToDateTime>;
  filler: FILLER;
  /**
   * If set, uses the provided filler to also fill in gaps for existing data
   * using lodash `merge`.
   */
  fillPartial?: boolean;
  unit: TimeUnit;
  step?: number;
  getKey?: (timestamp: DateTime) => Timestamp;
};

export const fillGaps = <
  VALUE extends TimeSeriesEntry<object>,
  FILLER = Filler<Partial<VALUE>>
>({
  timeSeries,
  dateRange,
  fillPartial,
  filler,
  unit,
  step = 1,
  getKey = (day): string => dayjs(day).toISOString(),
}: FillGapsArgs<VALUE, FILLER>): WithFilledGaps<VALUE, FILLER>[] => {
  const from = dayjs(dateRange.from);
  let to = dayjs(dateRange.to);

  const sortedTimeSeries = [...timeSeries].sort((a, b) =>
    dayjs(a.timestamp).isAfter(b.timestamp) ? 1 : -1
  );

  const getFiller =
    typeof filler === 'function'
      ? (filler as (timestamp: DateTime) => FilledValue<VALUE, FILLER>)
      : (timestamp: DateTime): FilledValue<VALUE, FILLER> =>
          ({
            ...filler,
            timestamp: getKey(timestamp),
          } as FilledValue<VALUE, FILLER>);

  // We have no data, fill everything with filler
  if (!sortedTimeSeries.length) {
    return timeRange(from, to, { unit, step }).map(getFiller);
  }

  // First timestamp to have a reference of to place rest of the entries
  const firstEntry = sortedTimeSeries[0];
  const lastEntry = sortedTimeSeries.at(-1)!;

  const firstTimestamp = dayjs(firstEntry.timestamp);
  const lastTimestamp = dayjs(lastEntry.timestamp);
  if (lastTimestamp.isAfter(to)) {
    to = lastTimestamp;
  }

  const result: WithFilledGaps<VALUE, FILLER>[] = [
    fillPartial ? merge({}, getFiller(firstTimestamp), firstEntry) : firstEntry,
  ];

  // Add fillers from first entry down to 'from' time
  let time = firstTimestamp.utc().subtract(step, unit);
  while (!time.isBefore(from)) {
    result.unshift(getFiller(time));
    time = time.subtract(step, unit);
  }

  time = firstTimestamp.utc().add(step, unit);
  let needle = 1; // Point to next entry as first entry is already in the results array
  while (!time.isAfter(to)) {
    const entry = sortedTimeSeries[needle];
    if (!entry || time.isBefore(entry.timestamp)) {
      result.push(getFiller(time));
    } else {
      result.push(fillPartial ? merge({}, getFiller(time), entry) : entry);
      needle++;
    }
    time = time.add(step, unit);
  }

  return result;
};

type RankDataArgs<T extends object> = {
  data: T[];
  prop: string | ((value: T) => string | number);
  maxRank?: number;
};
export const rankData = <T extends object>({
  data,
  prop,
  maxRank = 5,
}: RankDataArgs<T>): Ranked<T>[] => {
  const getProp =
    typeof prop === 'function'
      ? prop
      : (entry: T): string | number => get(entry, prop);

  const rankedData = orderBy(data, getProp, 'desc').map(
    cloneDeep
  ) as Ranked<T>[];

  for (let i = 0; i < rankedData.length; i++) {
    const entry = rankedData[i];
    const val = getProp(entry);
    entry.rank = i < maxRank && val != null ? i : Number.MAX_VALUE;
  }

  return rankedData;
};

export const getTopEntries = <T>(
  rankedData: Ranked<T>[],
  maxRank = 5
): Ranked<T>[] => {
  const topEntries: Ranked<T>[] = [];
  for (const entry of rankedData) {
    if (entry.rank < maxRank) {
      topEntries.push(entry);
    } else {
      break;
    }
  }
  return topEntries;
};
